/**
 * Structure & Partners Client Telemetry & Analytics Engine
 * Comprehensive user tracking, UTM capture, session duration, and device intelligence.
 */

import { forensicsTracker, ForensicsTelemetryState } from './useForensicsTracker';

export interface UserTelemetryData {
  visitorId: string;
  sessionId: string;
  timeOnSiteSeconds: number;
  timeOnSiteFormatted: string;
  referrer: string;
  referrerDomain: string;
  landingUrl: string;
  currentUrl: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  yclid?: string;
  gclid?: string;
  deviceType: 'Mobile' | 'Tablet' | 'Desktop';
  os: string;
  browser: string;
  language: string;
  timezone: string;
  localTime: string;
  screenResolution: string;
  viewportSize: string;
  colorDepth: number;
  devicePixelRatio: number;
  maxScrollDepthPercent: number;
  ymClientId?: string;
  forensics?: ForensicsTelemetryState;
}

let sessionStartTime = Date.now();
let maxScrollPercent = 0;

if (typeof window !== 'undefined') {
  // Track scroll depth
  window.addEventListener('scroll', () => {
    const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (totalHeight > 0) {
      const currentScroll = Math.min(100, Math.round((window.scrollY / totalHeight) * 100));
      if (currentScroll > maxScrollPercent) {
        maxScrollPercent = currentScroll;
      }
    }
  }, { passive: true });
}

export function getVisitorId(): string {
  if (typeof window === 'undefined') return 'SP-SSR';
  try {
    let vid = localStorage.getItem('sp_visitor_id');
    if (!vid) {
      vid = 'SP-UID-' + Math.random().toString(36).substring(2, 9).toUpperCase() + '-' + Date.now().toString(36).toUpperCase();
      localStorage.setItem('sp_visitor_id', vid);
    }
    return vid;
  } catch {
    return 'SP-ANON-' + Date.now();
  }
}

export function getSessionId(): string {
  if (typeof window === 'undefined') return 'SP-SES-SSR';
  try {
    let sid = sessionStorage.getItem('sp_session_id');
    if (!sid) {
      sid = 'SES-' + Math.random().toString(36).substring(2, 8).toUpperCase() + '-' + Date.now().toString(36).toUpperCase();
      sessionStorage.setItem('sp_session_id', sid);
    }
    return sid;
  } catch {
    return 'SES-EPHEMERAL';
  }
}

function getQueryParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((val, key) => {
    params[key] = val;
  });
  return params;
}

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : undefined;
}

function detectOS(ua: string): string {
  if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Windows NT 10.0|Windows NT 11.0/.test(ua)) return 'Windows 10/11';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown OS';
}

function detectBrowser(ua: string): string {
  if (/YaBrowser/i.test(ua)) return 'Yandex Browser';
  if (/Edg/i.test(ua)) return 'Microsoft Edge';
  if (/Chrome/i.test(ua) && !/Chromium|OPR|Edg/i.test(ua)) return 'Google Chrome';
  if (/Safari/i.test(ua) && !/Chrome|Chromium|Android/i.test(ua)) return 'Apple Safari';
  if (/Firefox/i.test(ua)) return 'Mozilla Firefox';
  if (/Opera|OPR/i.test(ua)) return 'Opera';
  return 'Other Browser';
}

function detectDeviceType(): 'Mobile' | 'Tablet' | 'Desktop' {
  if (typeof window === 'undefined') return 'Desktop';
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'Tablet';
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/i.test(ua)) {
    return 'Mobile';
  }
  return 'Desktop';
}

export function collectUserTelemetry(): UserTelemetryData {
  if (typeof window === 'undefined') {
    return {
      visitorId: 'SP-SSR',
      sessionId: 'SP-SSR',
      timeOnSiteSeconds: 0,
      timeOnSiteFormatted: '0с',
      referrer: 'Direct',
      referrerDomain: 'Direct',
      landingUrl: '',
      currentUrl: '',
      deviceType: 'Desktop',
      os: 'Server',
      browser: 'SSR',
      language: 'ru',
      timezone: 'Europe/Moscow',
      localTime: new Date().toLocaleString('ru-RU'),
      screenResolution: '0x0',
      viewportSize: '0x0',
      colorDepth: 24,
      devicePixelRatio: 1,
      maxScrollDepthPercent: 0
    };
  }

  const durationSec = Math.floor((Date.now() - sessionStartTime) / 1000);
  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  const timeFormatted = minutes > 0 ? `${minutes} мин ${seconds} сек` : `${seconds} сек`;

  const q = getQueryParams();
  const rawReferrer = document.referrer || '';
  let refDomain = 'Прямой заход (Direct)';
  if (rawReferrer) {
    try {
      refDomain = new URL(rawReferrer).hostname;
    } catch {
      refDomain = rawReferrer;
    }
  }

  const ua = navigator.userAgent;
  const ymUid = getCookie('_ym_uid');

  return {
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    timeOnSiteSeconds: durationSec,
    timeOnSiteFormatted: timeFormatted,
    referrer: rawReferrer || 'Прямой заход',
    referrerDomain: refDomain,
    landingUrl: window.location.origin + window.location.pathname,
    currentUrl: window.location.href,
    utmSource: q['utm_source'] || q['source'],
    utmMedium: q['utm_medium'],
    utmCampaign: q['utm_campaign'],
    utmTerm: q['utm_term'],
    utmContent: q['utm_content'],
    yclid: q['yclid'],
    gclid: q['gclid'],
    deviceType: detectDeviceType(),
    os: detectOS(ua),
    browser: detectBrowser(ua),
    language: navigator.language || 'ru',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow',
    localTime: new Date().toLocaleString('ru-RU', { timeZoneName: 'short' }),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    colorDepth: window.screen.colorDepth || 24,
    devicePixelRatio: window.devicePixelRatio || 1,
    maxScrollDepthPercent: maxScrollPercent,
    ymClientId: ymUid,
    forensics: typeof window !== 'undefined' ? forensicsTracker.getSnapshot() : undefined
  };
}
